import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { usePayerSignatureRequirementConfig } from "./configs/payer/usePayerSignatureRequirementConfig";
import { usePayeeSignatureRequirementConfig } from "./configs/payee/usePayeeSignatureRequirementConfig";
import { useMemo } from "react";
import { getSignedDocument } from "../../../../services/signedDocuments";
import { useWSQueries } from "@ws-react-query";
import { QUERY_SIGNED_DOCUMENT } from "../../../../query/signedDocuments/keys";
import {
  buildConfigFields,
  sharedRequirementsQueriesConfig
} from "../../utils";
import { getSignedDocumentFromCache } from "./configs/helpers/getSignedDocumentFromCache";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces/api/requirement";
import { ISignedDocumentResponse } from "@wingspanhq/signed-documents/dist/lib/interfaces";

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const queries = requirements
    .filter(
      requirement =>
        requirement.requirementType === RequirementType.Signature &&
        requirement.dataSourceId
    )
    .map(requirement => ({
      queryKey: [QUERY_SIGNED_DOCUMENT, requirement.dataSourceId],
      queryFn: () => getSignedDocument(requirement.dataSourceId),
      ...sharedRequirementsQueriesConfig
    }));

  return useWSQueries({ queries });
};

export const useSignatureRequirementTableConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.Signature] => {
  const contextQueries = useContextQueries(requirements);

  const payerSignatureRequirementConfig =
    usePayerSignatureRequirementConfig(callback);
  const payeeSignatureRequirementConfig =
    usePayeeSignatureRequirementConfig(callback);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  const fields = useMemo(
    buildConfigFields<ISignedDocumentResponse, RequirementType.Signature>({
      payerConfig: payerSignatureRequirementConfig,
      payeeConfig: payeeSignatureRequirementConfig,
      getAdditionalData: getSignedDocumentFromCache
    }),
    [queriesHash]
  );

  return {
    ...fields,
    getAdditionalData(requirement, context) {
      return getSignedDocumentFromCache(
        requirement as IRequirementResponse,
        context
      );
    },
    isLoading: contextQueries.some(query => query.isLoading),
    dataUpdatedAt: queriesHash
  };
};
